import { useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import { MagnifyingGlass, FloppyDisk, Drop } from "@phosphor-icons/react";

const PURCHASE_UNITS = ["kg", "g", "L", "ml", "m", "pz"];

const TYPE_TABS = [
  { key: "materia_prima", label: "Materie Prime" },
  { key: "imballaggio", label: "Imballaggi" },
];

export default function Listino() {
  const [items, setItems] = useState([]);
  const [activeType, setActiveType] = useState("materia_prima");
  const [search, setSearch] = useState("");
  const [edits, setEdits] = useState({}); // { [productId]: { purchase_cost, purchase_unit, density } }
  const [savingId, setSavingId] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/products", { params: { type: activeType } });
      setItems(data);
    } catch (e) {
      if (e?.response?.status !== 401) toast.error(formatApiError(e));
    }
  }, [activeType]);

  useEffect(() => {
    load();
  }, [load]);

  const filtered = items.filter((p) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return p.name.toLowerCase().includes(q) || p.barcode.includes(q);
  });

  const getEdit = (p, key) => {
    if (edits[p.id] && edits[p.id][key] !== undefined) return edits[p.id][key];
    if (key === "purchase_unit") return p.purchase_unit || p.unit;
    if (key === "density") return p.density ?? "";
    return p[key] ?? "";
  };

  const setEdit = (id, key, value) => {
    setEdits((prev) => ({
      ...prev,
      [id]: { ...(prev[id] || {}), [key]: value },
    }));
  };

  const isDirty = (id) => !!edits[id];

  const computeEffectivePerLitre = (p) => {
    const cost = Number(getEdit(p, "purchase_cost") || 0);
    if (cost <= 0) return null;
    const pUnit = (getEdit(p, "purchase_unit") || p.unit || "").toLowerCase();
    const usageUnit = (p.unit || "").toLowerCase();
    const density = Number(getEdit(p, "density") || 0);
    // Show €/L only if usage in L (or ml)
    if (usageUnit !== "l" && usageUnit !== "ml") return null;
    let costPerL = null;
    if (pUnit === "l") costPerL = cost;
    else if (pUnit === "ml") costPerL = cost * 1000;
    else if (pUnit === "kg" && density > 0) costPerL = cost * density;
    else if (pUnit === "g" && density > 0) costPerL = cost * 1000 * density;
    return costPerL;
  };

  // True when the user has filled `density` but `purchase_unit` matches the
  // usage unit, so density is effectively ignored. Helps the user understand
  // that they probably want to switch "Acquistato in" to kg/g.
  const densityIsIgnored = (p) => {
    const pUnit = (getEdit(p, "purchase_unit") || p.unit || "").toLowerCase();
    const usageUnit = (p.unit || "").toLowerCase();
    const density = Number(getEdit(p, "density") || 0);
    if (density <= 0) return false;
    return (
      (usageUnit === "l" && pUnit === "l") ||
      (usageUnit === "l" && pUnit === "ml") ||
      (usageUnit === "ml" && pUnit === "ml") ||
      (usageUnit === "ml" && pUnit === "l") ||
      (usageUnit === "kg" && pUnit === "kg") ||
      (usageUnit === "kg" && pUnit === "g") ||
      (usageUnit === "g" && pUnit === "g") ||
      (usageUnit === "g" && pUnit === "kg")
    );
  };

  const save = async (p) => {
    const e = edits[p.id];
    if (!e) return;
    const payload = {};
    if (e.purchase_cost !== undefined) payload.purchase_cost = Number(e.purchase_cost) || 0;
    if (e.purchase_unit !== undefined) payload.purchase_unit = e.purchase_unit || null;
    if (e.density !== undefined) {
      payload.density = e.density === "" || e.density === null ? null : Number(e.density);
    }
    setSavingId(p.id);
    try {
      await api.put(`/products/${p.id}`, payload);
      toast.success(`${p.name} aggiornato`);
      setEdits((prev) => {
        const { [p.id]: _omit, ...rest } = prev;
        return rest;
      });
      await load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSavingId(null);
    }
  };

  return (
    <div className="p-8 lg:p-12 fade-up" data-testid="listino-page">
      <div className="mb-8">
        <div className="text-[10px] uppercase tracking-[0.2em] text-[#4B5563] mb-2">
          Database Centralizzato · Costi di Acquisto
        </div>
        <h1 className="font-display text-4xl lg:text-5xl font-bold tracking-tight text-[#0A0A0A]">
          Listino MP & Imballaggi
        </h1>
        <p className="text-sm text-[#4B5563] mt-2 max-w-3xl">
          Inserisci qui i prezzi e (per i liquidi) il peso specifico delle materie prime
          e degli imballaggi. I valori vengono usati automaticamente nei calcoli costi
          delle formule e delle distinte plastiche.
        </p>
      </div>

      <div className="border-b border-[#E5E7EB] mb-6 flex gap-1">
        {TYPE_TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => {
              if (activeType === t.key) return;
              setActiveType(t.key);
              setEdits({});
              setSearch("");
            }}
            data-testid={`listino-tab-${t.key}`}
            className={`px-5 py-3 text-xs font-display font-semibold uppercase tracking-wider transition-colors border-b-2 ${
              activeType === t.key
                ? "border-[#1D3557] text-[#0A0A0A]"
                : "border-transparent text-[#4B5563] hover:text-[#0A0A0A]"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div className="flex items-center mb-6">
        <div className="relative w-full max-w-md">
          <MagnifyingGlass
            size={16}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-[#4B5563]"
          />
          <input
            data-testid="listino-search-input"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={activeType === "materia_prima" ? "Cerca materia prima…" : "Cerca imballaggio…"}
            className="w-full border border-[#E5E7EB] bg-white pl-9 pr-4 py-3 text-sm outline-none focus:ring-2 focus:ring-[#1D3557]"
          />
        </div>
        <div className="ml-auto text-xs text-[#4B5563] font-mono-data">
          {filtered.length} {activeType === "materia_prima" ? "materie prime" : "imballaggi"}
        </div>
      </div>

      <div className="bg-white border border-[#E5E7EB] overflow-x-auto" data-testid="listino-table">
        <table className="w-full">
          <thead className="bg-[#F9FAFB] border-b border-[#E5E7EB]">
            <tr className="text-left text-[10px] uppercase tracking-wider text-[#4B5563]">
              <th className="px-5 py-3 font-semibold">Materia Prima</th>
              <th className="px-5 py-3 font-semibold">Uso</th>
              <th className="px-5 py-3 font-semibold">Acquistato in</th>
              <th className="px-5 py-3 font-semibold text-right">Costo €</th>
              <th className="px-5 py-3 font-semibold text-right">Peso specifico</th>
              <th className="px-5 py-3 font-semibold text-right bg-[#EEF2F7]">€/L effettivo</th>
              <th className="px-5 py-3 font-semibold text-right">Salva</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr>
                <td colSpan={7} className="px-5 py-12 text-center text-sm text-[#4B5563]">
                  {activeType === "materia_prima"
                    ? "Nessuna materia prima trovata."
                    : "Nessun imballaggio trovato."}
                </td>
              </tr>
            ) : (
              filtered.map((p) => {
                const dirty = isDirty(p.id);
                const effL = computeEffectivePerLitre(p);
                const pUnit = (getEdit(p, "purchase_unit") || p.unit || "").toLowerCase();
                const needsDensity =
                  (p.unit === "L" || p.unit === "ml") && (pUnit === "kg" || pUnit === "g");
                return (
                  <tr
                    key={p.id}
                    className={`border-b border-[#E5E7EB] ${
                      dirty ? "bg-[#FFFBEB]" : "hover:bg-[#F9FAFB]"
                    }`}
                    data-testid={`listino-row-${p.id}`}
                  >
                    <td className="px-5 py-3 text-sm">
                      <div className="font-medium text-[#0A0A0A]">{p.name}</div>
                      <div className="font-mono-data text-[10px] text-[#4B5563]">
                        {p.barcode}
                      </div>
                    </td>
                    <td className="px-5 py-3 text-sm font-mono-data text-[#0A0A0A]">
                      {p.unit}
                    </td>
                    <td className="px-5 py-3">
                      <select
                        data-testid={`listino-purchase-unit-${p.id}`}
                        value={getEdit(p, "purchase_unit")}
                        onChange={(e) => setEdit(p.id, "purchase_unit", e.target.value)}
                        className="border border-[#E5E7EB] bg-white px-2 py-1.5 text-sm font-mono-data outline-none focus:ring-2 focus:ring-[#1D3557]"
                      >
                        {PURCHASE_UNITS.map((u) => (
                          <option key={u} value={u}>
                            {u}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td className="px-5 py-3 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <span className="text-xs text-[#4B5563]">€</span>
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          data-testid={`listino-cost-${p.id}`}
                          value={getEdit(p, "purchase_cost")}
                          onChange={(e) => setEdit(p.id, "purchase_cost", e.target.value)}
                          className="w-24 border border-[#E5E7EB] bg-white px-2 py-1.5 text-sm font-mono-data text-right outline-none focus:ring-2 focus:ring-[#1D3557]"
                        />
                        <span className="text-xs text-[#4B5563]">/ {pUnit}</span>
                      </div>
                    </td>
                    <td className="px-5 py-3 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          placeholder={needsDensity ? "richiesto" : "—"}
                          data-testid={`listino-density-${p.id}`}
                          value={getEdit(p, "density")}
                          onChange={(e) => setEdit(p.id, "density", e.target.value)}
                          className={`w-20 border bg-white px-2 py-1.5 text-sm font-mono-data text-right outline-none focus:ring-2 focus:ring-[#1D3557] ${
                            needsDensity && !getEdit(p, "density")
                              ? "border-[#F4A261] bg-[#FFF8E7]"
                              : "border-[#E5E7EB]"
                          }`}
                          title="Peso specifico (kg/L)"
                        />
                        <span className="text-[10px] text-[#4B5563]">kg/L</span>
                      </div>
                      {densityIsIgnored(p) && (
                        <div
                          className="text-[10px] text-[#F4A261] mt-1 leading-tight"
                          data-testid={`listino-density-ignored-${p.id}`}
                          title="Il peso specifico non viene applicato perché compri e usi nella stessa unità"
                        >
                          ⚠ ignorato — cambia "Acquistato in" → kg
                        </div>
                      )}
                    </td>
                    <td className="px-5 py-3 text-right bg-[#F8FBFC]">
                      {effL !== null ? (
                        <div className="flex items-center justify-end gap-1.5 text-[#2A9D8F]">
                          <Drop size={14} weight="duotone" />
                          <span className="font-mono-data text-sm font-semibold">
                            € {effL.toFixed(4)} /L
                          </span>
                        </div>
                      ) : p.unit === "kg" || p.unit === "g" ? (
                        <span className="font-mono-data text-xs text-[#4B5563]">
                          {Number(getEdit(p, "purchase_cost") || 0) > 0
                            ? `€ ${Number(getEdit(p, "purchase_cost")).toFixed(2)} / ${pUnit}`
                            : "—"}
                        </span>
                      ) : (
                        <span className="text-xs text-[#4B5563]">—</span>
                      )}
                    </td>
                    <td className="px-5 py-3 text-right">
                      <button
                        onClick={() => save(p)}
                        disabled={!dirty || savingId === p.id}
                        data-testid={`listino-save-${p.id}`}
                        className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs uppercase tracking-wider font-semibold transition-colors ${
                          dirty
                            ? "bg-[#1D3557] text-white hover:bg-[#0A0A0A]"
                            : "bg-[#F3F4F6] text-[#9CA3AF] cursor-not-allowed"
                        }`}
                      >
                        <FloppyDisk size={12} weight="bold" />
                        {savingId === p.id ? "…" : "Salva"}
                      </button>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4 text-xs text-[#4B5563]">
        <div className="bg-white border border-[#E5E7EB] p-4">
          <div className="font-display font-semibold text-[#0A0A0A] mb-1 uppercase tracking-wider text-[11px]">
            💡 Come funziona
          </div>
          <p>
            Inserisci il <b>costo</b> per unità di acquisto (es. €/kg). Se la MP è venduta a
            peso ma usata a volume nelle ricette, aggiungi il <b>peso specifico</b> (kg/L) e
            il sistema calcola automaticamente €/L.
          </p>
        </div>
        <div className="bg-white border border-[#E5E7EB] p-4">
          <div className="font-display font-semibold text-[#0A0A0A] mb-1 uppercase tracking-wider text-[11px]">
            🧪 Esempio pratico
          </div>
          <p>
            Ammonio Tiosolfato: peso specifico <b>1.3</b>, costo <b>€550/kg</b>.
            <br />
            Effettivo nelle formule: <b>€550 × 1.3 = €715/L</b>
          </p>
        </div>
        <div className="bg-white border border-[#E5E7EB] p-4">
          <div className="font-display font-semibold text-[#0A0A0A] mb-1 uppercase tracking-wider text-[11px]">
            🔄 Sincronizzazione
          </div>
          <p>
            I prezzi salvati qui vengono usati ovunque nelle formule (Distinte Base, costi
            di produzione, margini). <b>Single source of truth.</b>
          </p>
        </div>
      </div>
    </div>
  );
}
